/*
 * We need this function to parse a string to number .
 * This is needed because we need to parse the value of the input field to a number when not using the register function of react-hook-form.
 * Our types says that a field needs to be a number, but inputs return a string.
 */
export const getValidNumberValue = (value: string | number): number => {
  if (value == null) {
    return NaN;
  }

  if (typeof value === 'number') {
    return value;
  }
  let str = value.trim();
  if (str === '') {
    return NaN;
  }

  // Strict validation for invalid formats
  // 1. More than one dot or more than one comma (unless comma is thousands separator)
  // 2. Mixed or misplaced separators
  // 3. Only allow valid thousands and decimal patterns

  // If both comma and dot exist, comma is thousands separator, dot is decimal
  if (str.includes(',') && str.includes('.')) {
    // Validate: only valid if commas are in thousands positions
    const [intPart, decPart] = str.split('.');
    if (!/^(-?\d{1,3})(,\d{3})*$/.test(intPart)) {
      return NaN;
    }
    if (decPart && /[.,]/.test(decPart)) {
      return NaN;
    }
    str = str.replace(/,/g, '');
  } else if (str.includes(',')) {
    // Only comma exists
    const parts = str.split(',');
    if (parts.length > 1) {
      const last = parts[parts.length - 1];
      // If last group is 3 digits and all previous are 1 or 3, treat as thousands
      if (last.length === 3 && parts.slice(0, -1).every((p) => p.length === 1 || p.length === 3)) {
        // Validate: only valid if all groups are correct
        if (!/^(-?\d{1,3})(,\d{3})+$/.test(str)) {
          return NaN;
        }
        str = str.replace(/,/g, '');
      } else {
        // Otherwise, treat last comma as decimal separator
        // Only valid if at most one comma
        if (parts.length > 2) {
          return NaN;
        }
        str = str.replace(/,/g, '.');
      }
    } else {
      // Only one group, treat as decimal
      str = str.replace(/,/g, '.');
    }
  } else if ((str.match(/\./g) || []).length > 1) {
    // More than one dot
    return NaN;
  }

  // Remove any character except digits, minus, and dot
  if (/[^0-9.-]/.test(str)) {
    return NaN;
  }

  const finalNumber = parseFloat(str);
  // If parseFloat fails or input is malformed, return NaN
  if (isNaN(finalNumber)) {
    return NaN;
  }
  return finalNumber;
};

export const roundNumber = (num: number, decimals: number) => {
  if (decimals === 0) {
    return Math.round(num);
  }

  const multiplier = Math.pow(10, Math.round(decimals));
  return Math.round((num + Number.EPSILON) * multiplier) / multiplier;
};

export const countDecimalPlaces = (number: number) => {
  const decimalIndex = number.toString().indexOf('.');
  return decimalIndex >= 0 ? number.toString().length - decimalIndex - 1 : 0;
};
